/**
 * **What a `form` entry's declared fields are, read the same way at both ends** (`CMS-016` AC-1).
 *
 * ## Why this is a contract and not a copy in each application
 *
 * A form's fields are *content*: an editor adds a question in the console and no deploy happens.
 * So the marketing site and the API both have to read the same list out of the same opaque
 * `ContentData`, and they have to agree about it exactly — the site renders the fields, the API
 * closes the submitted answers against them, and a disagreement is a field somebody filled in and
 * the API refused, or worse, a field the API accepted that nobody could see.
 *
 * That is the definition of a shared HTTP contract, so it lives here rather than being written
 * twice. `AGENTS.md` puts the `zod` request/response shapes in this package for the same reason;
 * this one is a reader rather than a schema, because the thing being read is authored content
 * whose shape the type system cannot describe.
 *
 * ## Malformed fields are skipped, not thrown on
 *
 * The reader on the site is serving a stranger who wants to ask a question, and the reader on the
 * API is answering them. One mistyped field on the form entry must not turn either into a failure.
 * A skipped field is not offered and is not accepted — the two ends reach the same outcome
 * independently, which is the property that makes skipping safe.
 */
/**
 * The controls a form field may declare.
 *
 * Closed, and deliberately **not** the CMS's own `FIELD_CONTROLS`. That vocabulary is about
 * editing a content entry in the console and includes things a public form has no business
 * offering — `image`, `blocks`, `reference`, `richtext`. These six are what a visitor can be asked
 * for. A form declaring anything else is a form the renderer cannot draw, so it is skipped.
 */
export const FORM_FIELD_CONTROLS = [
    'text',
    'textarea',
    'email',
    'select',
    /**
     * **Many of a fixed set** — the 10 September demo form's *"When suits you?"*, four chips with
     * *"Pick all that work and we'll send times that fit."* above them.
     *
     * Added because nothing in this list could hold it. `select` is one of several; `checkbox` is a
     * single yes or no. Declaring four separate checkboxes would work and would be the wrong shape:
     * they would be four unrelated answers rather than one question, the API would store four keys,
     * and an editor adding a fifth time of day would be editing the form's *structure* rather than
     * a list of choices.
     *
     * Shares `options` with `select`, and is skipped the same way when it has none.
     */
    'checkboxGroup',
    'checkbox',
    'number',
];
/**
 * Read the field list off a form entry's content.
 *
 * Duplicate keys keep the first: a submission's answers are keyed by field key, so two fields
 * sharing one would mean the second silently overwriting the first's answer.
 */
export function declaredFormFields(form) {
    const raw = form['fields'];
    if (!Array.isArray(raw))
        return [];
    const fields = [];
    for (const candidate of raw) {
        if (candidate === null || typeof candidate !== 'object' || Array.isArray(candidate))
            continue;
        const row = candidate;
        const key = typeof row['key'] === 'string' ? row['key'].trim() : '';
        const label = typeof row['label'] === 'string' ? row['label'].trim() : '';
        const control = row['control'];
        if (key.length === 0 || label.length === 0)
            continue;
        if (typeof control !== 'string')
            continue;
        if (!FORM_FIELD_CONTROLS.includes(control))
            continue;
        if (fields.some((existing) => existing.key === key))
            continue;
        const options = readOptions(row['options']);
        if ((control === 'select' || control === 'checkboxGroup') && options.length === 0)
            continue;
        fields.push({
            key,
            label,
            control: control,
            required: row['required'] === true,
            ...(typeof row['placeholder'] === 'string' && row['placeholder'].trim() !== ''
                ? { placeholder: row['placeholder'].trim() }
                : {}),
            options,
        });
    }
    return fields;
}
/** An option needs a value to submit; the label falls back to the value so an editor who filled
 * in one of the two still gets a usable choice rather than a blank line in the list. */
function readOptions(raw) {
    if (!Array.isArray(raw))
        return [];
    const options = [];
    for (const candidate of raw) {
        if (candidate === null || typeof candidate !== 'object' || Array.isArray(candidate))
            continue;
        const row = candidate;
        const value = typeof row['value'] === 'string' ? row['value'].trim() : '';
        if (value.length === 0)
            continue;
        const label = typeof row['label'] === 'string' ? row['label'].trim() : '';
        options.push({ value, label: label.length > 0 ? label : value });
    }
    return options;
}
//# sourceMappingURL=cms-form-fields.js.map